import { useEffect, useMemo } from 'react';
import { useLazyGetAppPreferencesQuery, useUpdateAppPreferencesMutation } from '@/store/api/notificationApi';
import {
  setAllFiltersValues,
  setCurrentFiltersData,
  setCurrentFiltersValues,
  setInitialFiltersValues,
} from '@/store/slices/notificationPreferencesSlice/notificationPreferencesSlice';
import { useDispatch } from 'react-redux';
import {
  getNotificationPreferencesFiltersData,
  getNotificationPreferencesFiltersValues,
} from '@/store/slices/notificationPreferencesSlice/utils';
import { getNotificationPreferencesAssets } from '@/components/pages/SettingsPage/MainContent/NotificationPreferences/utils';
import { useAppFilters, useToast } from '@/hooks';
import { filterByFields } from '@/components/pages/SettingsPage/MainContent/utils';

export const useNotificationPreferences = () => {
  const { tab } = useAppFilters();
  const dispatch = useDispatch();
  const { toast } = useToast();
  const { search: searchValue } = useAppFilters();

  const [fetchAppPreferences, { data: notificationPreferencesData, isLoading, isFetching }] =
    useLazyGetAppPreferencesQuery();
  const [updateAppPreferences, { isLoading: isUpdating }] = useUpdateAppPreferencesMutation();

  const updateNotificationPreferences = async (payload: any) => {
    try {
      await updateAppPreferences(payload).unwrap();
      toast({
        variant: 'success',
        title: 'Success',
        description: `Notification preferences have been updated`,
      });
    } catch (e) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: `Failed to update notification preferences`,
      });
    }
  };

  const notificationPreferencesAssets = useMemo(() => {
    if (notificationPreferencesData) {
      const assets = getNotificationPreferencesAssets(notificationPreferencesData);
      const featureMap: Record<string, string> = {
        'health-status-change': 'health_status_change',
        'belt-misalignment': 'belt_misalignment',
        'rock-size': 'rock_size',
      };
      const assetsByType = tab ? assets?.filter((entry: any) => entry.feature_name === featureMap[tab]) : [];

      if (!assetsByType) return [];
      if (!searchValue) return assetsByType;

      const fieldsToSearch = ['asset_ui_name'];
      return filterByFields(assetsByType, searchValue, fieldsToSearch);
    }

    return [];
  }, [notificationPreferencesData, searchValue, tab]);

  const notificationPreferencesAssetsAllAssets = useMemo(() => {
    if (notificationPreferencesData) {
      const assets = getNotificationPreferencesAssets(notificationPreferencesData);

      if (!assets) return [];
      if (!searchValue) return assets;

      return filterByFields(assets, searchValue, ['asset_ui_name']);
    }

    return [];
  }, [notificationPreferencesData, searchValue]);

  useEffect(() => {
    const filtersValues = getNotificationPreferencesFiltersValues(notificationPreferencesAssets);
    const filtersData = getNotificationPreferencesFiltersData(
      notificationPreferencesAssets,
      filtersValues.currentFiltersValues,
      filtersValues.allFiltersValues
    );

    dispatch(setCurrentFiltersData(filtersData));
    dispatch(setAllFiltersValues(filtersValues.allFiltersValues));
    dispatch(setInitialFiltersValues(filtersValues.initialFiltersValues));
    dispatch(setCurrentFiltersValues(filtersValues.currentFiltersValues));
  }, [notificationPreferencesAssets]);

  useEffect(() => {
    fetchAppPreferences({}, true);
  }, []);

  return {
    notificationPreferencesAssets,
    notificationPreferencesAssetsAllAssets,
    isLoadingNotificationPreferences: isLoading || isFetching,
    updateNotificationPreferences,
    isUpdating,
  };
};
